import { Avatar, Box, CircularProgress, Container, Grid, Stack, Typography } from '@mui/material';

import { useLeadershipDirectory } from '@/hooks/use-public-site-modules';
import { useSiteOrgId } from '@/lib/site-org-context';
import type { LeadershipBlockContent } from '@/types/database';

// Bound to the real unit roster (see get_public_site_leadership) —
// updates automatically the moment a position changes hands in
// Scouting Ops, never manually re-typed. Adults only, name + position
// title only — no email/phone/address ever renders here; "contact this
// leader" is a separate masked-form concern, not this block's job.
export function LeadershipBlockView({ content }: { content: LeadershipBlockContent }) {
  const orgId = useSiteOrgId();
  const { data: leaders, isLoading } = useLeadershipDirectory(orgId);

  return (
    <Box sx={{ py: 6 }}>
      <Container maxWidth="md">
        {content.heading ? (
          <Typography variant="h4" sx={{ fontWeight: 700, textAlign: 'center', mb: 4 }}>
            {content.heading}
          </Typography>
        ) : null}

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} aria-label="Loading leadership directory" />
          </Box>
        ) : !leaders || leaders.length === 0 ? (
          <Typography color="text.secondary" sx={{ textAlign: 'center' }}>
            Leadership positions haven&apos;t been assigned yet.
          </Typography>
        ) : (
          <Grid container spacing={3}>
            {leaders.map((leader) => (
              <Grid key={leader.contact_id} size={{ xs: 12, sm: 6, md: 4 }}>
                <Stack spacing={1} sx={{ alignItems: 'center', textAlign: 'center' }}>
                  <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.main' }}>
                    {leader.first_name[0]}
                    {leader.last_name[0]}
                  </Avatar>
                  <Typography sx={{ fontWeight: 700 }}>
                    {leader.first_name} {leader.last_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {leader.position_title}
                  </Typography>
                </Stack>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
}
